import { useState } from 'react';
import { useTaskStore } from '../store/taskStore';
import { X } from 'lucide-react';
import QuillEditor from './QuillEditor';

interface NewTaskFormProps {
  onClose: () => void;
}

export default function NewTaskForm({ onClose }: NewTaskFormProps) {
  const { createTask } = useTaskStore();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!title.trim()) {
      setError('Görev başlığı gereklidir');
      return;
    }

    if (title.trim().length > 255) {
      setError('Görev başlığı 255 karakterden uzun olamaz');
      return;
    }

    if (createdBy.trim().length > 255) {
      setError('Oluşturan ismi 255 karakterden uzun olamaz');
      return;
    }

    setIsSubmitting(true);
    try {
      await createTask(
        title.trim(), 
        description.trim() || undefined,
        createdBy.trim() || undefined
      );
      onClose();
    } catch (error: any) {
      setError(error.message || 'Görev oluşturulamadı');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in">
      <div className="bg-white rounded-soft shadow-soft-xl max-w-4xl w-full p-6 animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Yeni Görev</h2>
          <button
            onClick={onClose}
            className="text-soft-muted hover:text-gray-900 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-900 mb-2">
              Başlık <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Görev başlığını girin"
              className="w-full px-4 py-2 border border-soft-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              autoFocus
              disabled={isSubmitting}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-900 mb-2">
              Açıklama
            </label>
            <QuillEditor
              value={description}
              onChange={setDescription}
              placeholder="Görev açıklaması yazın... (opsiyonel)"
              disabled={isSubmitting}
            />
          </div>

          {/* Created By */}
          <div>
            <label htmlFor="createdBy" className="block text-sm font-medium text-gray-900 mb-2">
              Oluşturan
            </label>
            <input
              type="text"
              id="createdBy"
              value={createdBy}
              onChange={(e) => setCreatedBy(e.target.value)}
              placeholder="İsminizi girin (opsiyonel)"
              className="w-full px-4 py-2 border border-soft-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-soft-border text-soft-text rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              İptal
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Oluşturuluyor...' : 'Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
